import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  createNotificationProvider,
  deleteNotificationProvider,
  listNotificationProviders,
  testNotificationProviderConnection,
  updateNotificationProvider,
} from '../../api/notificationSettingsApi';
import { settingsKeys, useNotificationSettings, useUpdateNotificationSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { NotificationChannel } from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  MultiToggleField,
  ProviderManager,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
} from '../components';
import { NOTIFICATION_CHANNEL_OPTIONS } from '../options';

/** Notification channels, templates, opt-in and providers. */
export function NotificationSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage, canManageCredentials } = useDomainPermissions('notifications');
  const query = useNotificationSettings();
  const update = useUpdateNotificationSettings();
  const form = useSettingsForm(query.data);
  const { feedback, notify, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success(t('notificationSettings.savedFeedback')),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('notificationSettings.title')}
      subtitle={t('notificationSettings.subtitle')}
      icon={<NotificationsRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('notificationSettings.channelsSection.title')} icon={<TuneRoundedIcon />}>
              <MultiToggleField
                label={t('notificationSettings.channelsSection.enabledChannelsLabel')}
                value={draft.enabledChannels}
                options={NOTIFICATION_CHANNEL_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) =>
                  form.setField('enabledChannels', value as NotificationChannel[])
                }
                disabled={disabled}
              />
              <SwitchField
                label={t('notificationSettings.channelsSection.orderTemplatesLabel')}
                description={t('notificationSettings.channelsSection.orderTemplatesDescription')}
                checked={draft.orderTemplatesEnabled}
                onChange={(value) => form.setField('orderTemplatesEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('notificationSettings.channelsSection.marketingLabel')}
                checked={draft.marketingEnabled}
                onChange={(value) => form.setField('marketingEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('notificationSettings.channelsSection.optInLabel')}
                description={t('notificationSettings.channelsSection.optInDescription')}
                checked={draft.optInRequired}
                onChange={(value) => form.setField('optInRequired', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <ProviderManager
              title={t('notificationSettings.providersTitle')}
              description={t('notificationSettings.providersDescription')}
              icon={<NotificationsRoundedIcon />}
              listKey={settingsKeys.notificationProviders}
              api={{
                list: listNotificationProviders,
                create: createNotificationProvider,
                update: updateNotificationProvider,
                remove: deleteNotificationProvider,
                test: testNotificationProviderConnection,
              }}
              canManage={canManage}
              canManageCredentials={canManageCredentials}
              credentialHint={t('notificationSettings.providersCredentialHint')}
              onFeedback={notify}
            />
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
